import { listOfBooks, verseCounts } from '../constants';

export const RECOVERY_VERSION_VERSE_TOTAL = 31103;

export const CANONICAL_BOOKS = Object.freeze(
  listOfBooks.map((name, index) => Object.freeze({
    bookNumber: index + 1,
    name,
    verseCounts: Object.freeze([...verseCounts[name]]),
  }))
);

const bookByNumber = new Map(CANONICAL_BOOKS.map(book => [book.bookNumber, book]));
const firstVerseKeyByBook = new Map();
const firstVerseKeyByChapter = new Map();

let nextVerseKey = 1;
CANONICAL_BOOKS.forEach(book => {
  firstVerseKeyByBook.set(book.bookNumber, nextVerseKey);
  book.verseCounts.forEach((count, chapterIndex) => {
    firstVerseKeyByChapter.set(`${book.bookNumber}:${chapterIndex + 1}`, nextVerseKey);
    nextVerseKey += count;
  });
});

if (nextVerseKey - 1 !== RECOVERY_VERSION_VERSE_TOTAL) {
  throw new Error(
    `Recovery Version canon contains ${nextVerseKey - 1} verses; expected ${RECOVERY_VERSION_VERSE_TOTAL}`
  );
}

export function isValidVerseTuple(tuple) {
  if (
    !Array.isArray(tuple)
    || tuple.length !== 3
    || tuple.some(value => !Number.isInteger(value))
  ) {
    return false;
  }

  const [bookNumber, chapter, verse] = tuple;
  const book = bookByNumber.get(bookNumber);
  return Boolean(
    book
    && chapter >= 1
    && chapter <= book.verseCounts.length
    && verse >= 1
    && verse <= book.verseCounts[chapter - 1]
  );
}

export function assertValidVerseTuple(tuple) {
  if (!isValidVerseTuple(tuple)) {
    throw new RangeError(`Invalid canonical verse tuple: ${JSON.stringify(tuple)}`);
  }
  return tuple;
}

export function verseTupleToKey(tuple) {
  assertValidVerseTuple(tuple);
  const [bookNumber, chapter, verse] = tuple;
  return firstVerseKeyByChapter.get(`${bookNumber}:${chapter}`) + verse - 1;
}

export function verseKeyToTuple(key) {
  if (
    !Number.isInteger(key)
    || key < 1
    || key > RECOVERY_VERSION_VERSE_TOTAL
  ) {
    throw new RangeError(
      `Invalid canonical verse key: ${key}. Expected an integer from 1 to ${RECOVERY_VERSION_VERSE_TOTAL}`
    );
  }

  let low = 0;
  let high = CANONICAL_BOOKS.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const book = CANONICAL_BOOKS[middle];
    const start = firstVerseKeyByBook.get(book.bookNumber);
    const end = start + book.verseCounts.reduce((sum, count) => sum + count, 0) - 1;
    if (key < start) {
      high = middle - 1;
    } else if (key > end) {
      low = middle + 1;
    } else {
      for (let chapterIndex = 0; chapterIndex < book.verseCounts.length; chapterIndex += 1) {
        const chapter = chapterIndex + 1;
        const chapterStart = firstVerseKeyByChapter.get(`${book.bookNumber}:${chapter}`);
        const chapterEnd = chapterStart + book.verseCounts[chapterIndex] - 1;
        if (key <= chapterEnd) {
          return [book.bookNumber, chapter, key - chapterStart + 1];
        }
      }
    }
  }

  throw new RangeError(`Unable to resolve canonical verse key: ${key}`);
}

export function compareVerseTuples(first, second) {
  return verseTupleToKey(first) - verseTupleToKey(second);
}

export function dedupeVerseTuples(tuples) {
  const seen = new Set();
  return tuples.reduce((result, tuple) => {
    const key = verseTupleToKey(tuple);
    if (!seen.has(key)) {
      seen.add(key);
      result.push([...tuple]);
    }
    return result;
  }, []);
}

export function sortVerseTuples(tuples) {
  return tuples.map(tuple => [...assertValidVerseTuple(tuple)]).sort(compareVerseTuples);
}

export function getCanonicalBook(bookNumber) {
  const book = bookByNumber.get(bookNumber);
  if (!book) {
    throw new RangeError(`Invalid canonical book number: ${bookNumber}`);
  }
  return book;
}

export function getChapterVerseCount(bookNumber, chapter) {
  const book = getCanonicalBook(bookNumber);
  if (!Number.isInteger(chapter) || chapter < 1 || chapter > book.verseCounts.length) {
    throw new RangeError(
      `${book.name} has chapters 1-${book.verseCounts.length}; received chapter ${chapter}`
    );
  }
  return book.verseCounts[chapter - 1];
}
